'use client';

import { useState } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Sparkles,
  DollarSign,
  Users,
  Globe,
  Building2,
  Calculator,
  ExternalLink,
  BookOpen,
  PieChart,
} from 'lucide-react';

import { EmailGateStep } from '@/components/tools/email-gate-step';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

type Step = 1 | 2 | 3 | 4 | 5 | 'email' | 'result';

const SOURCES = {
  costPerTicket: {
    name: 'Zendesk CX Trends Report',
    year: '2025',
    url: 'https://www.zendesk.com/cx-trends-report/',
  },
  surveypal: {
    name: 'Surveypal Industry Report',
    year: '2024',
    url: 'https://www.surveypal.com/blog/customer-service-statistics',
  },
  salaryData: {
    name: 'Glassdoor',
    year: '2024',
    url: 'https://www.glassdoor.com/Salaries/customer-support-representative-salary',
  },
  budgetPercentage: {
    name: 'Pacific Crest SaaS Survey',
    year: '2024',
    url: 'https://www.bvp.com/atlas/saas-survey-results',
  },
};

export function SupportBudgetPlanner() {
  const { t, isRTL } = useTranslation();

  // The model is denominated in USD. On Arabic it is displayed - and the AI
  // rate computed - in riyal at the 3.75 peg, so every figure the reader sees
  // is in one currency.
  const SAR_PER_USD = 3.75;
  const toDisplay = (usd: number) => (isRTL ? usd * SAR_PER_USD : usd);

  // Arabic-Indic digits for the Arabic locale, untouched otherwise.
  const num = (v: string | number) =>
    isRTL
      ? String(v)
          .replace(/[0-9]/g, (d) => '٠١٢٣٤٥٦٧٨٩'[Number(d)])
          // A no-break space groups thousands, so the comma is free to serve
          // as the decimal mark (Thmanyah has no U+066B glyph).
          .replace(/(?<=[٠-٩]),(?=[٠-٩])/g, '\u00A0')
          .replace(/(?<=[٠-٩])\.(?=[٠-٩])/g, ',')
      : String(v);

  // Takes a USD figure and renders it in the reader's currency.
  const money = (usd: number) => {
    const v = toDisplay(usd);
    // Cost-per-ticket bands carry a decimal ($2.70); budgets never do.
    const shown =
      v < 10 && !Number.isInteger(v)
        ? v.toFixed(1)
        : Math.round(v).toLocaleString('en-US');
    const n = num(shown);
    return isRTL ? `${n} ⃁` : `$${n}`;
  };
  const [step, setStep] = useState<Step>(1);
  const [monthlyTickets, setMonthlyTickets] = useState(5000);
  const [industry, setIndustry] = useState<string | null>(null);
  const [region, setRegion] = useState<string | null>(null);
  const [companySize, setCompanySize] = useState<string | null>(null);

  // Cost per ticket by industry (research-backed)
  const INDUSTRY_COSTS = {
    ecommerce: {
      label: t.tools_pages['E-commerce & Retail'],
      costPerTicket: { min: 2.7, max: 12, avg: 7 },
      icon: '🛒',
    },
    saas: {
      label: t.tools_pages['SaaS & Technology'],
      costPerTicket: { min: 25, max: 35, avg: 30 },
      icon: '💻',
    },
    fintech: {
      label: t.industries['Financial Services'],
      costPerTicket: { min: 15, max: 30, avg: 22 },
      icon: '🏦',
    },
    healthcare: {
      label: t.industries.Healthcare,
      costPerTicket: { min: 50, max: 60, avg: 55 },
      icon: '🏥',
    },
    telecom: {
      label: t.tools_pages['Telecommunications'],
      costPerTicket: { min: 20, max: 25, avg: 22 },
      icon: '📱',
    },
    travel: {
      label: t.tools_pages['Travel & Hospitality'],
      costPerTicket: { min: 10, max: 20, avg: 15 },
      icon: '✈️',
    },
    insurance: {
      label: t.tools_pages['Insurance'],
      costPerTicket: { min: 30, max: 50, avg: 40 },
      icon: '🛡️',
    },
    other: {
      label: t.demo['Other'],
      costPerTicket: { min: 13, max: 20, avg: 16 },
      icon: '🏢',
    },
  };

  // Regional salary data (annual, USD)
  const REGION_SALARIES = {
    us: {
      label: t.tools_pages['United States'],
      salary: 38000,
      range: isRTL
        ? '٩٨ - ١٧٢ ألف ⃁'
        : '$26K - $46K',
      currency: '$',
    },
    uk: {
      label: t.tools_pages['United Kingdom'],
      salary: 32000,
      range: '£22K - £35K',
      currency: '£',
    },
    eu_west: {
      label: t.tools_pages['Western Europe'],
      salary: 35000,
      range: '€28K - €42K',
      currency: '€',
    },
    eu_east: {
      label: t.tools_pages['Eastern Europe'],
      salary: 18000,
      range: '€12K - €24K',
      currency: '€',
    },
    latam: {
      label: t.tools_pages['Latin America'],
      salary: 15000,
      range: isRTL
        ? '٣٨ - ٧٥ ألف ⃁'
        : '$10K - $20K',
      currency: '$',
    },
    asia: {
      label: t.tools_pages['Asia (India, Philippines)'],
      salary: 8000,
      range: isRTL
        ? '١٩ - ٤٥ ألف ⃁'
        : '$5K - $12K',
      currency: '$',
    },
    mena: {
      label: t.tools_pages['GCC / Middle East'],
      salary: 22000,
      range: isRTL
        ? '٥٦ - ١١٢ ألف ⃁'
        : '$15K - $30K',
      currency: '$',
    },
  };

  // Company size affects overhead multiplier
  const COMPANY_SIZES = {
    startup: {
      label: t.tools_pages['Startup (1-50 employees)'],
      overheadMultiplier: 1.2,
      description: t.tools_pages['Lean operations, minimal overhead'],
    },
    smb: {
      label: t.tools_pages['SMB (51-200 employees)'],
      overheadMultiplier: 1.35,
      description: t.tools_pages['Growing infrastructure needs'],
    },
    midmarket: {
      label: t.tools_pages['Mid-Market (201-1000)'],
      overheadMultiplier: 1.5,
      description: t.tools_pages['Established processes, more tools'],
    },
    enterprise: {
      label: t.tools_pages['Enterprise (1000+)'],
      overheadMultiplier: 1.7,
      description: t.tools_pages['Full infrastructure, compliance needs'],
    },
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return monthlyTickets >= 100;
      case 2:
        return industry !== null;
      case 3:
        return region !== null;
      case 4:
        return companySize !== null;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (step === 4) {
      setStep('email');
    } else if (typeof step === 'number') {
      setStep((step + 1) as Step);
    }
  };

  const prevStep = () => {
    if (step === 'result') {
      setStep('email');
    } else if (step === 'email') {
      setStep(4);
    } else if (typeof step === 'number' && step > 1) {
      setStep((step - 1) as Step);
    }
  };

  const calculateBudget = () => {
    const industryData =
      INDUSTRY_COSTS[industry as keyof typeof INDUSTRY_COSTS];
    const regionData = REGION_SALARIES[region as keyof typeof REGION_SALARIES];
    const sizeData = COMPANY_SIZES[companySize as keyof typeof COMPANY_SIZES];

    if (!industryData || !regionData || !sizeData) {
      return null;
    }

    const yearlyTickets = monthlyTickets * 12;

    // Method 1: Cost per ticket approach
    const costPerTicketMin = industryData.costPerTicket.min * yearlyTickets;
    const costPerTicketMax = industryData.costPerTicket.max * yearlyTickets;
    const costPerTicketAvg = industryData.costPerTicket.avg * yearlyTickets;

    // Method 2: Headcount-based approach
    // Assuming 30 tickets/agent/day average, 22 working days/month
    const ticketsPerAgentPerMonth = 30 * 22;
    const agentsNeeded = Math.ceil(monthlyTickets / ticketsPerAgentPerMonth);

    // Total labor cost (salary + benefits ~30%)
    const benefitsMultiplier = 1.3;
    const laborCost = agentsNeeded * regionData.salary * benefitsMultiplier;

    // Add overhead (tools, training, management, facilities)
    const totalHeadcountBudget = laborCost * sizeData.overheadMultiplier;

    // Budget breakdown
    const laborPercentage = 0.65; // 65% typically goes to labor
    const toolsPercentage = 0.15; // 15% to tools/software
    const trainingPercentage = 0.08; // 8% to training
    const overheadPercentage = 0.12; // 12% to overhead/management

    // Use average of both methods for recommended budget
    const recommendedBudget = Math.round(
      (costPerTicketAvg + totalHeadcountBudget) / 2,
    );

    // With AI (70% automation, AI costs ~$0.90/resolution; the model is USD
    // throughout and only the display converts, so this is the USD rate)
    const aiAutomationRate = 0.7;
    const aiCostPerTicket = 0.9;
    const aiTickets = yearlyTickets * aiAutomationRate;

    const humanAgentsWithAI = Math.ceil(
      (monthlyTickets * (1 - aiAutomationRate)) / ticketsPerAgentPerMonth,
    );
    const aiCost = aiTickets * aiCostPerTicket;
    const humanLaborWithAI =
      humanAgentsWithAI * regionData.salary * benefitsMultiplier;
    const totalWithAI = humanLaborWithAI * sizeData.overheadMultiplier + aiCost;

    const savings = recommendedBudget - totalWithAI;
    const savingsPercentage = (savings / recommendedBudget) * 100;

    return {
      monthlyTickets,
      yearlyTickets,
      // Cost per ticket method
      costPerTicketMin,
      costPerTicketMax,
      costPerTicketAvg,
      industryData,
      // Headcount method
      agentsNeeded,
      laborCost,
      totalHeadcountBudget,
      regionData,
      sizeData,
      // Recommended
      recommendedBudget,
      monthlyBudget: Math.round(recommendedBudget / 12),
      // Breakdown
      laborAmount: Math.round(recommendedBudget * laborPercentage),
      toolsAmount: Math.round(recommendedBudget * toolsPercentage),
      trainingAmount: Math.round(recommendedBudget * trainingPercentage),
      overheadAmount: Math.round(recommendedBudget * overheadPercentage),
      // With AI
      humanAgentsWithAI,
      aiCost,
      humanLaborWithAI,
      totalWithAI: Math.round(totalWithAI),
      savings: Math.round(savings),
      savingsPercentage: Math.round(savingsPercentage),
    };
  };

  const results = step === 'result' ? calculateBudget() : null;

  const formatCurrency = (value: number) => {
    const v = toDisplay(value);
    if (v >= 1000000) {
      const m = num((v / 1000000).toFixed(2));
      return isRTL ? `${m} مليون ⃁` : `$${m}M`;
    }
    if (v >= 1000) {
      const k = num(Math.round(v / 1000));
      return isRTL ? `${k} ألف ⃁` : `$${k}K`;
    }
    return money(value);
  };

  return (
    <section className="min-h-screen bg-white pt-32 pb-20 md:pt-40">
      <div className="container max-w-3xl">
        {/* Progress */}
        {step !== 'result' && step !== 'email' && (
          <div className="mb-12">
            <div className="mb-2 flex justify-between text-sm text-neutral-400">
              <span>
                {t.tools_pages['Step']} {step} {t.tools_pages['of 4']}
              </span>
            </div>
            <div className="h-1 overflow-hidden rounded-full bg-neutral-100">
              <motion.div
                className="h-full bg-black"
                initial={{ width: 0 }}
                animate={{ width: `${((step as number) / 4) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          {/* Step 1: Monthly Tickets */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-green-100 px-4 py-2 text-sm font-medium text-green-700">
                  <DollarSign className="size-4" />
                  {t.tools_pages['Support Budget Planner']}
                </div>
                <h1 className="mb-2 text-3xl text-black md:text-4xl">
                  {t.tools_pages['How many tickets do you handle monthly?']}
                </h1>
                <p className="text-neutral-500">
                  {
                    t.tools_pages[
                      'This is the foundation for calculating your budget.'
                    ]
                  }
                </p>
              </div>

              <div className="space-y-6 rounded-2xl border border-neutral-200 p-8">
                <div className="text-center">
                  <div className="text-6xl font-bold text-black tabular-nums">
                    {monthlyTickets.toLocaleString()}
                  </div>
                  <div className="text-neutral-500">
                    {t.tools_pages['tickets per month']}
                  </div>
                </div>

                <input
                  type="range"
                  min="100"
                  max="100000"
                  step="100"
                  value={monthlyTickets}
                  onChange={(e) => setMonthlyTickets(Number(e.target.value))}
                  className="h-3 w-full cursor-pointer appearance-none rounded-full bg-neutral-100 accent-black"
                />

                <div className="flex justify-between text-sm text-neutral-400">
                  <span>100</span>
                  <span>100K</span>
                </div>
              </div>

              <div className="rounded-xl bg-green-50 p-4 text-sm text-green-800">
                <div className="flex items-start gap-2">
                  <BookOpen className="mt-0.5 size-4 shrink-0" />
                  <p>
                    <strong>{t.tools_pages['Industry insight:']}</strong>{' '}
                    {
                      t.tools_pages[
                        'Self-service resolutions cost $0.50-$2.37, while assisted support averages $13.50 per contact.'
                      ]
                    }
                    <a
                      href={SOURCES.costPerTicket.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${isRTL ? 'mr-1' : 'ml-1'} inline-flex items-center gap-1 underline`}
                    >
                      {t.tools_pages['Source'] || 'Source'}:{' '}
                      {SOURCES.costPerTicket.name}
                      <ExternalLink className="size-3" />
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Industry */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl text-black md:text-4xl">
                  {t.tools_pages['What industry are you in?']}
                </h1>
                <p className="text-neutral-500">
                  {
                    t.tools_pages[
                      'Support costs vary significantly by industry complexity.'
                    ]
                  }
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-black md:grid-cols-4">
                {Object.entries(INDUSTRY_COSTS).map(([id, data]) => (
                  <button
                    key={id}
                    onClick={() => setIndustry(id)}
                    className={`relative flex h-28 flex-col items-center justify-center gap-2 rounded-2xl border-2 p-3 text-center transition-all ${
                      industry === id
                        ? 'border-black bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    }`}
                  >
                    <div
                      className={`absolute top-3 right-3 flex size-5 items-center justify-center rounded-full transition-all ${
                        industry === id ? 'bg-black' : 'bg-transparent'
                      }`}
                    >
                      <Check
                        className={`size-3 transition-opacity ${industry === id ? 'text-white opacity-100' : 'opacity-0'}`}
                      />
                    </div>
                    <span className="text-2xl">{data.icon}</span>
                    <div>
                      <div className="text-xs leading-tight font-medium">
                        {data.label}
                      </div>
                      <div className="text-xs text-neutral-500">
                        {money(data.costPerTicket.min)}-
                        {money(data.costPerTicket.max)}/
                        {t.meta['tickets'] || 'ticket'}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="rounded-xl bg-green-50 p-4 text-sm text-green-800">
                <div className="flex items-start gap-2">
                  <BookOpen className="mt-0.5 size-4 shrink-0" />
                  <p>
                    <strong>{t.tools_pages['Cost benchmarks:']}</strong>{' '}
                    {
                      t.tools_pages[
                        'Healthcare averages $50-60/ticket due to complexity, while e-commerce averages $2.70-$12.'
                      ]
                    }
                    <a
                      href={SOURCES.surveypal.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${isRTL ? 'mr-1' : 'ml-1'} inline-flex items-center gap-1 underline`}
                    >
                      {t.tools_pages['Source'] || 'Source'}:{' '}
                      {SOURCES.surveypal.name}
                      <ExternalLink className="size-3" />
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Region */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl text-black md:text-4xl">
                  {t.tools_pages['Where is your support team located?']}
                </h1>
                <p className="text-neutral-500">
                  {
                    t.tools_pages[
                      'Labor costs are the biggest factor in support budgets.'
                    ]
                  }
                </p>
              </div>

              <div className="grid gap-4">
                {Object.entries(REGION_SALARIES).map(([id, data]) => (
                  <button
                    key={id}
                    onClick={() => setRegion(id)}
                    className={`flex items-center justify-between rounded-2xl border-2 p-5 transition-all ${
                      region === id
                        ? 'border-black bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    } ${isRTL ? 'text-right' : 'text-left'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`flex size-10 items-center justify-center rounded-xl ${
                          region === id
                            ? 'bg-black text-white'
                            : 'bg-neutral-100'
                        }`}
                      >
                        <Globe className="size-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-black">
                          {data.label}
                        </div>
                        <div className="text-sm text-neutral-500">
                          {t.tools_pages['Avg salary: {range}/year'].replace(
                            '{range}',
                            data.range,
                          )}
                        </div>
                      </div>
                    </div>
                    {region === id && <Check className="size-6 text-black" />}
                  </button>
                ))}
              </div>

              <div className="rounded-xl bg-green-50 p-4 text-sm text-green-800">
                <div className="flex items-start gap-2 text-black">
                  <BookOpen className="mt-0.5 size-4 shrink-0" />
                  <p>
                    <strong>{t.tools_pages['Salary data:']}</strong>{' '}
                    {
                      t.tools_pages[
                        'US agents average $34,547/year ($26K-$46K range). European salaries range €18K-€42K.'
                      ]
                    }
                    <a
                      href={SOURCES.salaryData.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${isRTL ? 'mr-1' : 'ml-1'} inline-flex items-center gap-1 underline`}
                    >
                      {t.tools_pages['Source'] || 'Source'}:{' '}
                      {SOURCES.salaryData.name}
                      <ExternalLink className="size-3" />
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 4: Company Size */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <div>
                <h1 className="mb-2 text-3xl text-black md:text-4xl">
                  {t.tools_pages["What's your company size?"]}
                </h1>
                <p className="text-neutral-500">
                  {
                    t.tools_pages[
                      'Larger companies have higher overhead costs.'
                    ]
                  }
                </p>
              </div>

              <div className="grid gap-4">
                {Object.entries(COMPANY_SIZES).map(([id, data]) => (
                  <button
                    key={id}
                    onClick={() => setCompanySize(id)}
                    className={`flex items-center justify-between rounded-2xl border-2 p-6 transition-all ${
                      companySize === id
                        ? 'border-black bg-neutral-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    } ${isRTL ? 'text-right' : 'text-left'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`flex size-10 items-center justify-center rounded-xl ${
                          companySize === id
                            ? 'bg-black text-white'
                            : 'bg-neutral-100'
                        }`}
                      >
                        <Building2 className="size-5" />
                      </div>
                      <div>
                        <div className="font-semibold text-black">
                          {data.label}
                        </div>
                        <div className="text-sm text-neutral-500">
                          {data.description}
                        </div>
                      </div>
                    </div>
                    {companySize === id && (
                      <Check className="size-6 text-black" />
                    )}
                  </button>
                ))}
              </div>

              <div className="rounded-xl bg-green-50 p-4 text-sm text-green-800">
                <div className="flex items-start gap-2">
                  <BookOpen className="mt-0.5 size-4 shrink-0 text-black" />
                  <p className="text-black">
                    <strong>{t.tools_pages['Budget insight:']}</strong>{' '}
                    {
                      t.tools_pages[
                        'Support operations typically account for ~0.7% of total company revenue in B2B SaaS.'
                      ]
                    }
                    <a
                      href={SOURCES.budgetPercentage.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${isRTL ? 'mr-1' : 'ml-1'} inline-flex items-center gap-1 text-black underline`}
                    >
                      {t.tools_pages['Source'] || 'Source'}:{' '}
                      {SOURCES.budgetPercentage.name}
                      <ExternalLink className="size-3" />
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Email Gate */}
          {step === 'email' && (
            <EmailGateStep
              key="email"
              tool="Support Budget Planner"
              onSubmit={() => setStep('result')}
              onBack={prevStep}
            />
          )}

          {/* Result */}
          {step === 'result' && results && (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-8"
            >
              <div className="text-center">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-green-100 px-4 py-2 text-sm font-medium text-green-700">
                  <Calculator className="size-4" />
                  {t.tools_pages['Your Support Budget Estimate']}
                </div>
                <h1 className="text-4xl text-black md:text-5xl">
                  {t.tools_pages['Recommended Annual Budget']}
                </h1>
              </div>

              {/* Main result */}
              <div className="rounded-3xl bg-black p-10 text-center text-white">
                <div className="mb-2 text-6xl font-bold tracking-tight md:text-7xl">
                  {formatCurrency(results.recommendedBudget)}
                </div>
                <div className="text-xl text-neutral-300">
                  {t.tools_pages['per year']}
                </div>
                <div className="mt-4 text-sm text-neutral-500">
                  {formatCurrency(results.monthlyBudget)}/
                  {t.footer['month'] || 'month'} •{' '}
                  {t.tools_pages['{agents} agents estimated'].replace(
                    '{agents}',
                    results.agentsNeeded.toString(),
                  )}
                </div>
              </div>

              {/* With AI comparison */}
              <div className="rounded-2xl border-2 border-dashed border-green-300 bg-green-50 p-6">
                <div className="flex items-start gap-4 text-black">
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-green-100">
                    <Sparkles className="size-6 text-green-600" />
                  </div>
                  <div className={isRTL ? 'text-right' : 'text-left'}>
                    <h3 className="text-lg text-neutral-900">
                      {t.tools_pages['With AI Automation (70% automated)']}
                    </h3>
                    <p className="mt-1 text-neutral-600">
                      {
                        t.tools_pages[
                          'Reduce your budget while handling the same volume:'
                        ]
                      }
                    </p>
                    <div className="mt-3 flex flex-wrap items-baseline gap-3 text-black">
                      <span className="text-4xl font-bold text-green-600">
                        {formatCurrency(results.totalWithAI)}
                      </span>
                      <span className="text-neutral-500">
                        /{t.footer['year'] || 'year'}
                      </span>
                      <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700">
                        {t.tools_pages['Save {amount} ({percentage}%)']
                          .replace('{amount}', formatCurrency(results.savings))
                          .replace(
                            '{percentage}',
                            results.savingsPercentage.toString(),
                          )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Budget breakdown */}
              <div className="space-y-4">
                <h3 className="flex items-center gap-2 text-xl text-black">
                  <PieChart className="size-5" />
                  {t.tools_pages['Budget Breakdown']}
                </h3>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-neutral-200 bg-white p-5">
                    <div className="mb-2 text-sm text-neutral-500">
                      {t.tools_pages['Labor (65%)']}
                    </div>
                    <div className="text-2xl font-bold text-black">
                      {formatCurrency(results.laborAmount)}
                    </div>
                    <div className="mt-1 text-xs text-neutral-400">
                      {t.tools_pages['Salaries, benefits, contractors']}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-neutral-200 bg-white p-5">
                    <div className="mb-2 text-sm text-neutral-500">
                      {t.tools_pages['Tools & Software (15%)']}
                    </div>
                    <div className="text-2xl font-bold text-black">
                      {formatCurrency(results.toolsAmount)}
                    </div>
                    <div className="mt-1 text-xs text-neutral-400">
                      {t.tools_pages['Helpdesk, CRM, analytics']}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-neutral-200 bg-white p-5">
                    <div className="mb-2 text-sm text-neutral-500">
                      {t.tools_pages['Training (8%)']}
                    </div>
                    <div className="text-2xl font-bold text-black">
                      {formatCurrency(results.trainingAmount)}
                    </div>
                    <div className="mt-1 text-xs text-neutral-400">
                      {t.tools_pages['Onboarding, ongoing education']}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-neutral-200 bg-white p-5">
                    <div className="mb-2 text-sm text-neutral-500">
                      {t.tools_pages['Overhead (12%)']}
                    </div>
                    <div className="text-2xl font-bold text-black">
                      {formatCurrency(results.overheadAmount)}
                    </div>
                    <div className="mt-1 text-xs text-neutral-400">
                      {t.tools_pages['Management, facilities, misc']}
                    </div>
                  </div>
                </div>
              </div>

              {/* Calculation methodology */}
              <div className="space-y-4">
                <h3 className="flex items-center gap-2 text-xl text-black">
                  <Calculator className="size-5" />
                  {t.tools_pages['How We Calculated This']}
                </h3>

                <div className="overflow-hidden rounded-2xl border border-neutral-200">
                  <div className="divide-y divide-neutral-100 bg-white">
                    <div className="bg-neutral-50 p-4">
                      <div className="font-semibold text-neutral-700">
                        {t.tools_pages['Method 1: Cost per Ticket']}
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-4 text-black">
                      <span className="text-neutral-600">
                        {t.tools_pages['Yearly tickets']}
                      </span>
                      <span className="font-mono font-semibold">
                        {results.yearlyTickets.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 text-black">
                      <span className="text-neutral-600">
                        {t.tools_pages[
                          'Industry cost/ticket ({label})'
                        ].replace('{label}', results.industryData.label)}
                      </span>
                      <span className="font-mono font-semibold">
                        {money(results.industryData.costPerTicket.avg)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 text-black">
                      <span className="text-neutral-600">
                        {t.tools_pages['Estimated by cost/ticket']}
                      </span>
                      <span className="font-mono font-semibold">
                        {formatCurrency(results.costPerTicketAvg)}
                      </span>
                    </div>

                    <div className="bg-neutral-50 p-4">
                      <div className="font-semibold text-neutral-700">
                        {t.tools_pages['Method 2: Headcount Based']}
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-4 text-black">
                      <span className="text-neutral-600">
                        {t.tools_pages['Base agents needed']}
                      </span>
                      <span className="font-mono font-semibold">
                        {results.agentsNeeded}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 text-black">
                      <span className="text-neutral-600">
                        {t.tools_pages['Avg salary ({label})'].replace(
                          '{label}',
                          results.regionData.label,
                        )}
                      </span>
                      <span className="font-mono font-semibold">
                        {money(results.regionData.salary)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-4 text-black">
                      <span className="text-neutral-600">
                        {t.tools_pages[
                          '+ Benefits (30%) + Overhead ({percentage}%)'
                        ].replace(
                          '{percentage}',
                          (
                            (results.sizeData.overheadMultiplier - 1) *
                            100
                          ).toFixed(0),
                        )}
                      </span>
                      <span className="font-mono font-semibold">
                        {formatCurrency(results.totalHeadcountBudget)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between bg-black p-4 text-white">
                      <span className="font-semibold">
                        {t.tools_pages['Recommended (average of both methods)']}
                      </span>
                      <span className="font-mono text-xl font-bold">
                        {formatCurrency(results.recommendedBudget)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sources */}
              <div className="rounded-2xl bg-neutral-50 p-6">
                <h4 className="mb-4 flex items-center gap-2 text-neutral-900">
                  <BookOpen className="size-4" />
                  {t.tools_pages['Research Sources']}
                </h4>
                <div className="grid gap-3 text-sm md:grid-cols-2">
                  {Object.entries(SOURCES).map(([key, source]) => (
                    <a
                      key={key}
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-neutral-600 hover:text-black"
                    >
                      <ExternalLink className="size-3" />
                      {source.name} ({source.year})
                    </a>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="space-y-4">
                <Link
                  href="/tools/team-size-calculator"
                  prefetch={true}
                  className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-black bg-white py-4 text-lg font-semibold text-black transition-all hover:bg-neutral-50"
                >
                  <Users className="size-5" />
                  {t.tools_pages['Calculate exact team size needed']}
                </Link>
                <Link
                  href="/demo"
                  prefetch={true}
                  aria-label={t.common['Learn more about Open and book a demo']}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-black py-4 text-lg font-semibold text-white transition-all hover:bg-neutral-800"
                >
                  {t.tools_pages['See how AI can reduce your budget']}
                  <ArrowRight
                    className={`size-5 ${isRTL ? 'rotate-180' : ''}`}
                  />
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        {step !== 'result' && step !== 'email' && (
          <div className="mt-12 flex items-center justify-between">
            <button
              onClick={prevStep}
              disabled={step === 1}
              className={`flex items-center gap-2 rounded-full px-6 py-3 font-medium transition-all ${
                step === 1
                  ? 'cursor-not-allowed text-neutral-300'
                  : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              <ArrowLeft className={`size-5 ${isRTL ? 'rotate-180' : ''}`} />
              {t.tools_pages['Back']}
            </button>

            <button
              onClick={nextStep}
              disabled={!canProceed()}
              className={`flex items-center gap-2 rounded-full px-8 py-3 font-semibold transition-all ${
                canProceed()
                  ? 'bg-black text-white hover:bg-neutral-800'
                  : 'cursor-not-allowed bg-neutral-200 text-neutral-400'
              }`}
            >
              {step === 4
                ? t.tools_pages['Calculate my budget']
                : t.tools_pages['Continue']}
              <ArrowRight className={`size-5 ${isRTL ? 'rotate-180' : ''}`} />
            </button>
          </div>
        )}

        {step === 'result' && (
          <div className="mt-8 text-center">
            <button
              onClick={() => setStep(1)}
              className="text-neutral-500 underline hover:text-neutral-700"
            >
              {t.tools_pages['Start over']}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
